import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { SCHOOL_NAV } from "./SchoolDashboard";
import api from "@/lib/api";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { toast } from "sonner";
import { Download } from "lucide-react";

const COLORS = ["#2563EB", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4"];

export default function Reports() {
  const [pos, setPos] = useState([]);
  const [budgets, setBudgets] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/purchase-orders"), api.get("/budgets")]).then(([p, b]) => { setPos(p.data); setBudgets(b.data); });
  }, []);
  const byCategory = budgets.map((b) => ({ name: b.category, value: b.utilized || 0 })).filter((x) => x.value > 0);
  // De-duplicate POs by supplier_ref (anonymised) — never expose vendor_name to schools
  const byVendor = Object.values(pos.reduce((acc, p) => {
    const key = p.supplier_ref || "Unassigned";
    acc[key] = acc[key] || { name: key, value: 0 };
    acc[key].value += p.total;
    return acc;
  }, {}));
  // Category spend from POs (grouped by canonical category, robust to legacy fragile strings)
  const spendByCat = Object.values(pos.reduce((acc, p) => {
    const cat = p.category && String(p.category).trim() ? p.category : "Uncategorized";
    acc[cat] = acc[cat] || { name: cat, value: 0 };
    acc[cat].value += p.total;
    return acc;
  }, {}));

  // CSV formula-injection safe: neutralize any cell starting with = + - @ tab CR
  // then RFC-4180 quote if it contains delimiter, quote, or newline.
  const csvCell = (v) => {
    let s = v === null || v === undefined ? "" : String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    if (/[",\r\n]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
    return s;
  };

  const exportCSV = () => {
    const rows = [
      ["PO Number", "Supplier Ref", "Category", "Amount", "Status"],
      ...pos.map((p) => [p.po_number, p.supplier_ref, p.category, p.total, p.status]),
    ];
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "schoollane-po-report.csv"; a.click();
    toast.success("Exported CSV");
  };

  return (
    <DashboardLayout nav={SCHOOL_NAV} title="Reports & Analytics">
      <div className="flex justify-end mb-4">
        <button data-testid="export-csv" onClick={exportCSV} className="inline-flex items-center gap-2 rounded-md bg-brand-blue text-white font-semibold px-4 py-2 hover:bg-brand-blueDark transition-colors duration-200"><Download size={16} /> Export CSV</button>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="sl-card p-5">
          <h3 className="font-heading font-semibold">Category-wise spend</h3>
          <div className="h-72 mt-2">
            {byCategory.length === 0 ? <div className="text-slate-500 py-16 text-center">No spend recorded yet.</div> :
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byCategory} dataKey="value" nameKey="name" outerRadius={90}>
                    {byCategory.map((c, i) => <Cell key={`cat-${c.name}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>}
          </div>
        </div>
        <div className="sl-card p-5">
          <h3 className="font-heading font-semibold">Vendor-wise spend</h3>
          <div className="h-72 mt-2">
            {byVendor.length === 0 ? <div className="text-slate-500 py-16 text-center">No POs yet.</div> :
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byVendor} dataKey="value" nameKey="name" outerRadius={90}>
                    {byVendor.map((v, i) => <Cell key={`v-${v.name}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
